import React, { useState } from 'react';
import Modal from './Modal';
import DatePicker from './DatePicker';
import { api } from '../api';

export default function Onboarding({ user, onFinish }) {
  // Режимы: welcome (спросить про инструкцию) → instructions → setup
  const [mode, setMode] = useState('welcome');
  const [step, setStep] = useState(1);

  // Данные
  const [accountName, setAccountName] = useState('Основной');
  const [accountBalance, setAccountBalance] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [salary, setSalary] = useState('');
  const [advance, setAdvance] = useState('');
  const [expenses, setExpenses] = useState([{ name: 'Коммуналка', amount: '' }]);

  // Закрыть онбординг (крестик): помечаем пройденным, чтобы больше не показывался
  const finish = () => {
    if (user && user.id) localStorage.setItem(`kopilka_onboard_${user.id}`, '1');
    else localStorage.setItem('kopilka_onboard', '1');
    onFinish && onFinish();
  };

  // ===== Инструкция простым языком (одна короткая страница) =====
  const wantInstructions = () => setMode('instructions');
  const skipInstructions = () => { setStep(1); setMode('setup'); };

  // ===== Шаги настройки =====
  const saveStep1 = async () => {
    try {
      await api.createAccount({ name: accountName });
      const accounts = await api.getAccounts();
      const acc = accounts.find(a => a.name === accountName) || accounts[0];
      if (acc && Number(accountBalance) > 0) {
        await api.createTransaction({
          type: 'income', amount: Number(accountBalance),
          date: new Date().toISOString().slice(0, 10),
          description: 'Начальный баланс', accountId: acc.id, periodId: null,
        });
      }
    } catch (e) { console.error(e); }
  };

  const saveStep2 = async () => {
    try {
      const period = { startDate, endDate, plannedIncomes: [], plannedExpenses: [] };
      if (Number(salary) > 0) period.plannedIncomes.push({ category: 'Зарплата', amount: Number(salary) });
      if (Number(advance) > 0) period.plannedIncomes.push({ category: 'Аванс', amount: Number(advance) });
      await api.createPeriod(period);
    } catch (e) { console.error(e); }
  };

  const saveStep3 = async () => {
    try {
      const periods = await api.getPeriods();
      const active = periods[0];
      if (active) {
        const updated = { ...active };
        updated.plannedExpenses = (updated.plannedExpenses || []).concat(
          expenses.map(e => ({ category: e.name || 'Обязательный платёж', amount: Number(e.amount) || 0 }))
        );
        await api.updatePeriod(active.id, updated);
      }
    } catch (e) { console.error(e); }
    finish();
  };

  const nextSetup = async () => {
    if (step === 1) { await saveStep1(); setStep(2); }
    else if (step === 2) { await saveStep2(); setStep(3); }
    else if (step === 3) { await saveStep3(); }
  };
  const prevSetup = () => {
    if (step > 1) setStep(s => s - 1);
    else { setStep(1); setMode('welcome'); }
  };

  const handleAddExpense = () => setExpenses(prev => [...prev, { name: '', amount: '' }]);
  const handleExpenseChange = (i, field, value) => {
    setExpenses(prev => prev.map((it, idx) => idx === i ? { ...it, [field]: value } : it));
  };

  return (
    <Modal title="Добро пожаловать" onClose={finish}>
      <div>
        {mode === 'welcome' && (
          <div>
            <h3 style={{ marginTop: 0 }}>Привет! 👋</h3>
            <p>Это приложение поможет считать бюджет, чтобы на всё хватило.</p>
            <p>Хотите короткую инструкцию, как всё работает?</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={wantInstructions}>Да, покажи</button>
              <button className="secondary" onClick={skipInstructions}>Нет, сразу к настройке</button>
            </div>
          </div>
        )}

        {mode === 'instructions' && (
          <div>
            <h3 style={{ marginTop: 0 }}>Как это работает</h3>
            <ul className="onb-list">
              <li><b>Период</b> — время, обычно от зарплаты до зарплаты.</li>
              <li><b>Суточный лимит</b> = доход ÷ дни периода. Пример: 100 000 ₽ ÷ 30 дней ≈ 3 333 ₽ в день.</li>
              <li>Дополнительные <b>доходы</b> лимит увеличивают, запланированные <b>траты</b> — уменьшают.</li>
              <li><b>Главное правило:</b> не выходи за суточный лимит — перетратил, завтра будет меньше. Сэкономил — завтра больше.</li>
            </ul>
            <p className="onb-note">📊 Дальше — быстрая настройка: счёт, период с доходами и обязательные траты. Займёт 1–2 минуты.</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
              <button className="secondary" onClick={skipInstructions}>Пропустить</button>
              <button onClick={skipInstructions}>К настройке</button>
            </div>
          </div>
        )}

        {mode === 'setup' && step === 1 && (
          <div>
            <h3 style={{ marginTop: 0 }}>Шаг 1 — Добавим счёт</h3>
            <p>Это основной счёт, на который вы будете записывать операции.</p>
            <div className="form-group">
              <label>Название счёта</label>
              <input value={accountName} onChange={e => setAccountName(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Сколько сейчас на нём денег?</label>
              <input type="number" inputMode="decimal" value={accountBalance} onChange={e => setAccountBalance(e.target.value)} />
            </div>
            <p className="onb-note">🔒 Эти данные хранятся только у вас и нужны только для корректных расчётов. Мы никому их не передаём.</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="secondary" onClick={prevSetup}>Назад</button>
              <button onClick={nextSetup}>Далее</button>
            </div>
          </div>
        )}

        {mode === 'setup' && step === 2 && (
          <div>
            <h3 style={{ marginTop: 0 }}>Шаг 2 — Период и доходы</h3>
            <p>Укажите период расчёта (например, с зарплаты до зарплаты) и ваши регулярные доходы.</p>
            <div className="form-group">
              <label>Дата начала</label>
              <DatePicker value={startDate} onChange={setStartDate} />
            </div>
            <div className="form-group">
              <label>Дата окончания</label>
              <DatePicker value={endDate} onChange={setEndDate} />
            </div>
            <div className="form-group">
              <label>Обычно в этом периоде вы получаете (зарплата)</label>
              <input type="number" inputMode="decimal" value={salary} onChange={e => setSalary(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Аванс (если бывает)</label>
              <input type="number" inputMode="decimal" value={advance} onChange={e => setAdvance(e.target.value)} />
            </div>
            <p className="onb-note">🔒 Данные о доходах хранятся только у вас и нужны только для расчётов.</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="secondary" onClick={prevSetup}>Назад</button>
              <button onClick={nextSetup}>Далее</button>
            </div>
          </div>
        )}

        {mode === 'setup' && step === 3 && (
          <div>
            <h3 style={{ marginTop: 0 }}>Шаг 3 — Обязательные траты</h3>
            <p>Добавьте регулярные обязательные траты: коммуналка, интернет и т.п. Они уменьшат суточный лимит.</p>
            {expenses.map((e, i) => (
              <div key={i} className="form-group" style={{ display: 'flex', gap: 8 }}>
                <input placeholder="Название" value={e.name} onChange={(ev) => handleExpenseChange(i, 'name', ev.target.value)} />
                <input type="number" inputMode="decimal" placeholder="Сумма" value={e.amount} onChange={(ev) => handleExpenseChange(i, 'amount', ev.target.value)} />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button type="button" className="secondary" onClick={handleAddExpense}>Добавить ещё</button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="secondary" onClick={prevSetup}>Назад</button>
              <button onClick={nextSetup}>Завершить</button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

